import React, { useState, useEffect } from 'react';
import { Gamepad2, ChevronLeft, ChevronRight, Sparkles } from '../lib/icons';
import ImageSlot from './ImageSlot';
import ContentSlot from './ContentSlot';

const CustomControllers = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const controllers = [
    { slotId: 'homepage-ctrl-blush-pink',    image: '/IMG_3721.JPEG',        alt: 'Pink PS5 DualSense controller with custom shell',                                                 title: 'Blush Pink'      },
    { slotId: 'homepage-ctrl-lakers',        image: '/IMG_3734.JPEG',        alt: 'Purple and yellow PS5 controller with custom buttons',                                            title: 'Lakers Edition'  },
    { slotId: 'homepage-ctrl-super-station', image: '/img_3330.jpeg',        alt: 'Super Station custom controller with colorful buttons',                                           title: 'Super Station'   },
    { slotId: 'homepage-ctrl-purple-carbon', image: '/img_3347.jpeg',        alt: 'Purple carbon fiber PS5 controller',                                                              title: 'Purple Carbon'   },
    { slotId: 'homepage-ctrl-hex-elite',     image: '/img_3218.jpeg',        alt: 'Back paddle upgrade with adaptive triggers - go from traditional triggers to hair click triggers', title: 'Hex Elite'       },
    { slotId: 'homepage-ctrl-iron-man',      image: '/img_3358.jpeg',        alt: 'Red and gold chrome PS5 controller with premium finish',                                          title: 'Iron Man Edition'},
    { slotId: 'homepage-ctrl-arctic-silver', image: '/img_3363.jpeg',        alt: 'Silver and white PS5 controller with gray gradient sides',                                        title: 'Arctic Silver'   },
    { slotId: 'homepage-ctrl-gold-prestige', image: '/img_3368.jpeg',        alt: 'Gold and black PS5 controller with carbon fiber touchpad',                                        title: 'Gold Prestige'   },
    { slotId: 'homepage-ctrl-blue-phantom',  image: '/img_3438.jpeg',        alt: 'Transparent blue PS5 controller with carbon fiber touchpad',                                      title: 'Blue Phantom'    },
    { slotId: 'homepage-ctrl-urban-camo',    image: '/img_3481.jpeg',        alt: 'Gray camouflage pattern PS5 controller',                                                          title: 'Urban Camo'      },
  ];

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % controllers.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, controllers.length]);

  const goToNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % controllers.length);
  };

  const goToPrevious = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + controllers.length) % controllers.length);
  };

  const goToSlide = (index: number) => {
    setIsAutoPlaying(false);
    setCurrentIndex(index);
  };

  return (
    <section className="bg-gradient-to-b from-[#0D0D0D] to-black py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNiIgc3Ryb2tlPSIjMzlGRjE0IiBzdHJva2Utd2lkdGg9IjAuNSIgb3BhY2l0eT0iMC4xIi8+PC9nPjwvc3ZnPg==')] opacity-20"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-[#39FF14]/10 border border-[#39FF14]/30 rounded-full px-6 py-2 mb-6">
            <Sparkles className="h-5 w-5 text-[#39FF14]" />
            <span className="text-[#39FF14] font-semibold">
              <ContentSlot slotId="homepage-ctrl-badge" fallback="NEW SERVICE" contentType="label" pageSection="homepage-controllers" label="Section Badge" />
            </span>
          </div>
          <h2 className="text-5xl font-bold text-[#E0E0E0] mb-4">
            <ContentSlot slotId="homepage-ctrl-heading" fallback="Custom Controllers" contentType="heading" pageSection="homepage-controllers" label="Section Heading" />
          </h2>
          <ContentSlot
            slotId="homepage-ctrl-subtitle"
            fallback="Level up your game with professionally customized controllers built to your exact specifications"
            contentType="paragraph"
            as="p"
            pageSection="homepage-controllers"
            label="Section Subtitle"
            className="text-xl text-[#B0B0B0] max-w-3xl mx-auto"
          />
        </div>

        <div className="space-y-12">
          <div className="relative max-w-5xl mx-auto">
            <div className="relative aspect-[16/9] rounded-2xl overflow-hidden bg-gray-900 border-2 border-[#39FF14]/30 shadow-2xl">
              <ImageSlot
                slotId={controllers[currentIndex].slotId}
                fallbackSrc={controllers[currentIndex].image}
                fallbackAlt={controllers[currentIndex].alt}
                className="object-cover"
                wrapperClassName="w-full h-full"
              />

              <button
                onClick={goToPrevious}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full backdrop-blur-sm transition-all duration-200 hover:scale-110"
                aria-label="Previous controller"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>

              <button
                onClick={goToNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full backdrop-blur-sm transition-all duration-200 hover:scale-110"
                aria-label="Next controller"
              >
                <ChevronRight className="h-6 w-6" />
              </button>

              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 backdrop-blur-sm px-4 py-2 rounded-full">
                <p className="text-white font-semibold">{controllers[currentIndex].title}</p>
              </div>
            </div>

            <div className="flex justify-center gap-2 mt-6">
              {controllers.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? 'w-8 bg-[#39FF14]'
                      : 'w-2 bg-gray-600 hover:bg-gray-500'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>

          <div className="space-y-8 max-w-4xl mx-auto">
            <div className="text-center">
              <h3 className="text-3xl font-bold text-[#E0E0E0] mb-6">
                <ContentSlot slotId="homepage-ctrl-subheading" fallback="Your Controller, Your Way" contentType="subheading" pageSection="homepage-controllers" label="Subheading" />
              </h3>
              <ContentSlot
                slotId="homepage-ctrl-description"
                fallback="We offer professional controller customization services. Whether you want to customize your existing controller or start with a brand new one, we can bring your vision to life."
                contentType="paragraph"
                as="p"
                pageSection="homepage-controllers"
                label="Main Description"
                className="text-[#B0B0B0] text-lg leading-relaxed mb-8"
              />
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex flex-col items-center text-center bg-gray-900/50 p-6 rounded-lg border border-gray-800">
                <div className="bg-[#00BFFF]/20 p-3 rounded-lg mb-4">
                  <Gamepad2 className="h-6 w-6 text-[#00BFFF]" />
                </div>
                <h4 className="text-[#E0E0E0] font-semibold mb-2">
                  <ContentSlot slotId="homepage-ctrl-feat1-title" fallback="Custom Shells & Trim" contentType="heading" pageSection="homepage-controllers" label="Feature 1 Title" />
                </h4>
                <p className="text-[#B0B0B0] text-sm">
                  <ContentSlot slotId="homepage-ctrl-feat1-desc" fallback="Swap front or rear shells, custom trim pieces, and button colors to match your style" contentType="paragraph" pageSection="homepage-controllers" label="Feature 1 Description" />
                </p>
              </div>

              <div className="flex flex-col items-center text-center bg-gray-900/50 p-6 rounded-lg border border-gray-800">
                <div className="bg-[#39FF14]/20 p-3 rounded-lg mb-4">
                  <Gamepad2 className="h-6 w-6 text-[#39FF14]" />
                </div>
                <h4 className="text-[#E0E0E0] font-semibold mb-2">
                  <ContentSlot slotId="homepage-ctrl-feat2-title" fallback="Hall Effect & GuliKit TMR Joysticks" contentType="heading" pageSection="homepage-controllers" label="Feature 2 Title" />
                </h4>
                <p className="text-[#B0B0B0] text-sm">
                  <ContentSlot slotId="homepage-ctrl-feat2-desc" fallback="Eliminate stick drift forever with magnetic sensor technology" contentType="paragraph" pageSection="homepage-controllers" label="Feature 2 Description" />
                </p>
              </div>

              <div className="flex flex-col items-center text-center bg-gray-900/50 p-6 rounded-lg border border-gray-800">
                <div className="bg-[#FF1744]/20 p-3 rounded-lg mb-4">
                  <Gamepad2 className="h-6 w-6 text-[#FF1744]" />
                </div>
                <h4 className="text-[#E0E0E0] font-semibold mb-2">
                  <ContentSlot slotId="homepage-ctrl-feat3-title" fallback="Performance Upgrades" contentType="heading" pageSection="homepage-controllers" label="Feature 3 Title" />
                </h4>
                <p className="text-[#B0B0B0] text-sm">
                  <ContentSlot slotId="homepage-ctrl-feat3-desc" fallback="Clicky buttons, hair triggers, and back paddle remap kits for competitive gaming" contentType="paragraph" pageSection="homepage-controllers" label="Feature 3 Description" />
                </p>
              </div>
            </div>

            <div className="pt-6 text-center">
              <a
                href="/#contact"
                className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#FF1744] to-[#39FF14] hover:from-[#39FF14] hover:to-[#FF1744] text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
              >
                <Gamepad2 className="h-5 w-5" />
                <span><ContentSlot slotId="homepage-ctrl-cta" fallback="Design Your Custom Controller" contentType="button" pageSection="homepage-controllers" label="CTA Button" /></span>
              </a>
              <p className="text-[#B0B0B0] text-sm mt-3">
                <ContentSlot slotId="homepage-ctrl-cta-note" fallback="Contact us to discuss your custom build. Prices vary based on customization options." contentType="label" pageSection="homepage-controllers" label="CTA Note" />
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CustomControllers;
